import React from "react";
import { useTranslation } from "react-i18next";
import { FiChevronDown, FiSearch } from "react-icons/fi";
import { IoClose } from "react-icons/io5";
import {
  EMPTY_LIBRARY_FILTERS,
  FilterOption,
  LibraryFilterChip,
  LibraryFilterFacets,
  LibraryFilterKey,
  LibraryFilterState,
} from "./libraryTypes";
import {
  PRICE_PRESETS,
  activeFilterChips,
  formatFilterButtonLabel,
  moreFilterCount,
} from "./filterUtils";
import { matchQuickCategories } from "./quickCategories";
import { CategoryHierarchyMenu } from "./CategoryHierarchyMenu";

type Props = {
  search: string;
  onSearchChange: (value: string) => void;
  searchRef: React.Ref<HTMLInputElement>;
  filters: LibraryFilterState;
  onChange: (next: LibraryFilterState) => void;
  facets: LibraryFilterFacets;
  sortBy: "name" | "priceLow" | "priceHigh" | "updatedAt";
  onSortChange: (value: "name" | "priceLow" | "priceHigh" | "updatedAt") => void;
  productCount: number;
  loading: boolean;
  onClose: () => void;
  showAllFilterControls?: boolean;
};

const FILTER_BUTTON =
  "inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 text-xs text-gray-700 hover:border-gray-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#111]";
const FILTER_BUTTON_ACTIVE =
  "inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border border-[#1A1B1D] bg-[#1A1B1D] px-2.5 text-xs text-white hover:bg-[#0D0C0C] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#111]";

const toggleId = (ids: string[], id: string) =>
  ids.includes(id) ? ids.filter((value) => value !== id) : [...ids, id];

const FilterMenu: React.FC<{
  id: string;
  openId: string | null;
  setOpenId: (id: string | null) => void;
  label: string;
  count: number;
  children: React.ReactNode;
  widthClass?: string;
  panelClassName?: string;
}> = ({
  id,
  openId,
  setOpenId,
  label,
  count,
  children,
  widthClass = "w-64",
  panelClassName,
}) => {
  const open = openId === id;
  return (
    <div
      className="relative shrink-0"
      data-filter-menu-open={open ? "true" : undefined}
    >
      <button
        type="button"
        onClick={() => setOpenId(open ? null : id)}
        className={count > 0 ? FILTER_BUTTON_ACTIVE : FILTER_BUTTON}
        aria-expanded={open}
      >
        {formatFilterButtonLabel(label, count)}
        <FiChevronDown size={12} />
      </button>
      {open && (
        <div
          className={
            panelClassName ??
            `absolute left-0 z-30 mt-1 max-h-72 overflow-auto rounded-md border border-gray-200 bg-white p-2 shadow-lg ${widthClass}`
          }
        >
          {children}
        </div>
      )}
    </div>
  );
};

const OptionList: React.FC<{
  options: FilterOption[];
  selected: string[];
  onToggle: (id: string) => void;
  searchable?: boolean;
}> = ({ options, selected, onToggle, searchable }) => {
  const { t } = useTranslation();
  const [query, setQuery] = React.useState("");
  const filtered = query.trim()
    ? options.filter((option) =>
        option.name.toLowerCase().includes(query.trim().toLowerCase()),
      )
    : options;
  return (
    <div className="space-y-1">
      {searchable && (
        <label className="relative mb-2 block">
          <span className="sr-only">{t("common.search")}</span>
          <FiSearch className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className="h-8 w-full rounded-md border border-gray-300 pl-7 pr-2 text-xs text-[#111] outline-none focus:border-[#111]"
          />
        </label>
      )}
      {filtered.map((option) => {
        const active = selected.includes(option.id);
        return (
          <button
            key={option.id}
            type="button"
            onClick={() => onToggle(option.id)}
            className={`flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-xs ${
              active ? "bg-[#1A1B1D] text-white" : "text-gray-800 hover:bg-gray-50"
            }`}
          >
            <span className="truncate">{option.name}</span>
            {option.logo && (
              <img src={option.logo} alt="" className="ml-2 h-4 max-w-[72px] object-contain" />
            )}
          </button>
        );
      })}
      {filtered.length === 0 && (
        <p className="px-2 py-3 text-xs text-gray-500">{t("productLibrary.noMatchingOptions")}</p>
      )}
    </div>
  );
};

export const ProductLibraryFilters: React.FC<Props> = ({
  search,
  onSearchChange,
  searchRef,
  filters,
  onChange,
  facets,
  sortBy,
  onSortChange,
  productCount,
  loading,
  onClose,
  showAllFilterControls = false,
}) => {
  const { t, i18n } = useTranslation();
  const [openId, setOpenId] = React.useState<string | null>(null);
  const [showMore, setShowMore] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const pills = matchQuickCategories(facets.categories);
  const chips = activeFilterChips(filters, facets, {
    locale: i18n.language,
    t,
  });
  const extraFacetCount = moreFilterCount(facets);

  React.useEffect(() => {
    if (!openId) return;

    const handlePointerDown = (event: PointerEvent) => {
      const openMenu = rootRef.current?.querySelector(
        '[data-filter-menu-open="true"]',
      );
      if (!openMenu?.contains(event.target as Node)) setOpenId(null);
    };
    document.addEventListener("pointerdown", handlePointerDown, true);
    return () =>
      document.removeEventListener("pointerdown", handlePointerDown, true);
  }, [openId]);

  const selectFilter = (next: LibraryFilterState) => {
    setOpenId(null);
    onChange(next);
  };

  const removeChip = (chip: LibraryFilterChip) => {
    if (chip.key === "price") {
      onChange({ ...filters, priceFrom: undefined, priceTo: undefined });
      return;
    }
    const map: Record<Exclude<LibraryFilterKey, "price">, keyof LibraryFilterState> = {
      category: "categoryIds",
      brand: "brandNames",
      supplier: "supplierIds",
      color: "colorIds",
      shape: "shapeIds",
      material: "materialIds",
      style: "styleIds",
      pattern: "patternIds",
      room: "roomIds",
    };
    const field = map[chip.key];
    const current = filters[field] as string[];
    onChange({ ...filters, [field]: current.filter((id) => id !== chip.id) });
  };

  // Prefer loading over a stale previous total (Apollo keeps prior data while
  // a new search/filter request is in flight — that total is often ≥ 1000).
  const countLabel = loading
    ? t("common.loadingProducts")
    : productCount >= 1000
      ? t("common.moreThanProducts")
      : t("productLibrary.productsCount", { count: productCount });

  return (
    <div ref={rootRef} className="space-y-4">
      <div className="flex items-stretch gap-2">
        <label className="relative block min-w-0 flex-1">
          <span className="sr-only">{t("productLibrary.searchProducts")}</span>
          <FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
          <input
            ref={searchRef}
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder={t("productLibrary.describeLookingFor")}
            className="h-10 w-full rounded-md border border-gray-300 bg-white pl-9 pr-3 text-sm text-[#111] outline-none placeholder:text-gray-400 focus:border-[#111] focus:ring-1 focus:ring-[#111]"
          />
        </label>
        <button
          type="button"
          onClick={onClose}
          aria-label={t("productLibrary.closeProductLibrary")}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:border-gray-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#111]"
        >
          <IoClose size={18} />
        </button>
      </div>

      {pills.length > 0 && (
        <div className="border-t border-gray-100 pt-3">
          <div className="mb-2 text-[11px] font-medium uppercase tracking-wide text-gray-500">
            {t("productLibrary.popularCategories")}
          </div>
          <div className="flex flex-wrap gap-2">
            {pills.map((pill) => {
              const selected = pill.matches.every((match) =>
                filters.categoryIds.includes(match.id),
              );
              return (
                <button
                  key={pill.key}
                  type="button"
                  onClick={() => {
                    const ids = pill.matches.map((match) => match.id);
                    const next = selected
                      ? filters.categoryIds.filter((id) => !ids.includes(id))
                      : Array.from(new Set([...filters.categoryIds, ...ids]));
                    onChange({ ...filters, categoryIds: next });
                  }}
                  className={`rounded-full border px-3 py-1.5 text-xs leading-snug ${
                    selected
                      ? "border-black bg-black text-white"
                      : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
                  }`}
                >
                  {t(`quickCategories.${pill.key}`)}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {(showAllFilterControls || facets.categories.length > 0) && (
          <FilterMenu
            id="categories"
            openId={openId}
            setOpenId={setOpenId}
            label={t("productLibrary.categories")}
            count={filters.categoryIds.length}
            panelClassName="absolute left-0 z-30 mt-1 max-h-[min(420px,70vh)] overflow-hidden rounded-md border border-gray-200 bg-white shadow-xl"
          >
            <CategoryHierarchyMenu
              roots={facets.categories}
              selected={filters.categoryIds}
              onToggle={(id) =>
                selectFilter({
                  ...filters,
                  categoryIds: toggleId(filters.categoryIds, id),
                })
              }
            />
          </FilterMenu>
        )}
        {(showAllFilterControls || facets.brands.length > 0) && (
          <FilterMenu
            id="brands"
            openId={openId}
            setOpenId={setOpenId}
            label={t("productLibrary.brands")}
            count={filters.brandNames.length}
          >
            <OptionList
              options={facets.brands}
              selected={filters.brandNames}
              onToggle={(id) =>
                selectFilter({
                  ...filters,
                  brandNames: toggleId(filters.brandNames, id),
                })
              }
              searchable
            />
          </FilterMenu>
        )}
        {(showAllFilterControls || facets.suppliers.length > 0) && (
          <FilterMenu
            id="retailers"
            openId={openId}
            setOpenId={setOpenId}
            label={t("productLibrary.retailers")}
            count={filters.supplierIds.length}
          >
            <OptionList
              options={facets.suppliers}
              selected={filters.supplierIds}
              onToggle={(id) =>
                selectFilter({
                  ...filters,
                  supplierIds: toggleId(filters.supplierIds, id),
                })
              }
              searchable
            />
          </FilterMenu>
        )}
        {(showAllFilterControls || facets.colors.length > 0) && (
          <FilterMenu
            id="color"
            openId={openId}
            setOpenId={setOpenId}
            label={t("productLibrary.colour")}
            count={filters.colorIds.length}
          >
            <OptionList
              options={facets.colors}
              selected={filters.colorIds}
              onToggle={(id) =>
                selectFilter({
                  ...filters,
                  colorIds: toggleId(filters.colorIds, id),
                })
              }
            />
          </FilterMenu>
        )}
        <FilterMenu
          id="price"
          openId={openId}
          setOpenId={setOpenId}
          label={t("productLibrary.price")}
          count={filters.priceFrom != null || filters.priceTo != null ? 1 : 0}
          widthClass="w-72"
        >
          <div className="space-y-2">
            <div className="flex flex-wrap gap-1.5">
              {PRICE_PRESETS.map((preset) => (
                <button
                  key={preset.labelKey}
                  type="button"
                  onClick={() =>
                    selectFilter({
                      ...filters,
                      priceFrom: preset.from,
                      priceTo:
                        preset.to === "max"
                          ? facets.priceMax ?? undefined
                          : preset.to,
                    })
                  }
                  className="rounded-full border border-gray-300 px-2.5 py-1 text-[11px] text-gray-700 hover:border-gray-400"
                >
                  {t(preset.labelKey)}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input
                aria-label={t("productLibrary.minPrice")}
                type="number"
                min={0}
                value={filters.priceFrom ?? ""}
                placeholder={
                  facets.priceMin != null
                    ? `${t("productLibrary.minPricePlaceholder")} ${Math.round(facets.priceMin)}`
                    : t("productLibrary.minPricePlaceholder")
                }
                onChange={(event) =>
                  onChange({
                    ...filters,
                    priceFrom: event.target.value
                      ? Number(event.target.value)
                      : undefined,
                  })
                }
                className="h-8 rounded-md border border-gray-300 px-2 text-xs text-[#111]"
              />
              <input
                aria-label={t("productLibrary.maxPrice")}
                type="number"
                min={0}
                value={filters.priceTo ?? ""}
                placeholder={
                  facets.priceMax != null
                    ? `${t("productLibrary.maxPricePlaceholder")} ${Math.round(facets.priceMax)}`
                    : t("productLibrary.maxPricePlaceholder")
                }
                onChange={(event) =>
                  onChange({
                    ...filters,
                    priceTo: event.target.value
                      ? Number(event.target.value)
                      : undefined,
                  })
                }
                className="h-8 rounded-md border border-gray-300 px-2 text-xs text-[#111]"
              />
            </div>
          </div>
        </FilterMenu>
        {(showAllFilterControls || extraFacetCount > 0) && (
          <button
            type="button"
            onClick={() => setShowMore((current) => !current)}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1.5 text-xs text-gray-600 hover:bg-gray-50 hover:text-gray-800"
          >
            {t("productLibrary.moreFilters")}
          </button>
        )}
      </div>

      {showMore && (showAllFilterControls || extraFacetCount > 0) && (
        <div className="flex flex-wrap items-center gap-2">
          {(showAllFilterControls || facets.shapes.length > 0) && (
            <FilterMenu id="shape" openId={openId} setOpenId={setOpenId} label={t("productLibrary.shape")} count={filters.shapeIds.length}>
              <OptionList options={facets.shapes} selected={filters.shapeIds} onToggle={(id) => selectFilter({ ...filters, shapeIds: toggleId(filters.shapeIds, id) })} />
            </FilterMenu>
          )}
          {(showAllFilterControls || facets.materials.length > 0) && (
            <FilterMenu id="material" openId={openId} setOpenId={setOpenId} label={t("productLibrary.material")} count={filters.materialIds.length}>
              <OptionList options={facets.materials} selected={filters.materialIds} onToggle={(id) => selectFilter({ ...filters, materialIds: toggleId(filters.materialIds, id) })} />
            </FilterMenu>
          )}
          {(showAllFilterControls || facets.styles.length > 0) && (
            <FilterMenu id="style" openId={openId} setOpenId={setOpenId} label={t("common.style")} count={filters.styleIds.length}>
              <OptionList options={facets.styles} selected={filters.styleIds} onToggle={(id) => selectFilter({ ...filters, styleIds: toggleId(filters.styleIds, id) })} />
            </FilterMenu>
          )}
          {(showAllFilterControls || facets.patterns.length > 0) && (
            <FilterMenu id="pattern" openId={openId} setOpenId={setOpenId} label={t("common.pattern")} count={filters.patternIds.length}>
              <OptionList options={facets.patterns} selected={filters.patternIds} onToggle={(id) => selectFilter({ ...filters, patternIds: toggleId(filters.patternIds, id) })} />
            </FilterMenu>
          )}
          {(showAllFilterControls || facets.rooms.length > 0) && (
            <FilterMenu id="room" openId={openId} setOpenId={setOpenId} label={t("productLibrary.room")} count={filters.roomIds.length}>
              <OptionList options={facets.rooms} selected={filters.roomIds} onToggle={(id) => selectFilter({ ...filters, roomIds: toggleId(filters.roomIds, id) })} />
            </FilterMenu>
          )}
        </div>
      )}

      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {chips.map((chip) => (
            <button
              key={`${chip.key}-${chip.id}`}
              type="button"
              onClick={() => removeChip(chip)}
              className="flex items-center gap-1.5 rounded-full border border-gray-300 bg-gray-100 px-2.5 py-1 text-xs text-[#1A1B1D]"
            >
              {chip.label}
              <IoClose size={12} />
            </button>
          ))}
          <button
            type="button"
            onClick={() => onChange({ ...EMPTY_LIBRARY_FILTERS })}
            className="text-xs text-gray-600 underline-offset-2 hover:underline"
          >
            {t("productLibrary.clearAllFilters")}
          </button>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 text-xs text-gray-600">
        <span>{countLabel}</span>
        <label className="flex items-center gap-2">
          {t("common.sort")}
          <select
            value={sortBy}
            onChange={(event) =>
              onSortChange(event.target.value as typeof sortBy)
            }
            className="h-8 rounded-md border border-gray-300 bg-white py-0 text-xs text-[#111] focus:border-[#111] focus:ring-[#111]"
          >
            <option value="name">{t("common.name")}</option>
            <option value="updatedAt">{t("productLibrary.recentlyUpdated")}</option>
            <option value="priceLow">{t("productLibrary.priceLowHigh")}</option>
            <option value="priceHigh">{t("productLibrary.priceHighLow")}</option>
          </select>
        </label>
      </div>
    </div>
  );
};
